/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import { Table, Spinner, Breadcrumb } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useApi } from "../../../Controller/ApiController";
import { Download } from "react-bootstrap-icons";
import { setToast } from "../../../Util/Toast";
import CustomButton from "../../../Components/Button/CustomButton";

const BalanceSheet = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { apiGet } = useApi();
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const companyType = searchParams.get("companytype");
  const isBalanceSheetPage = location.pathname.includes("/balance-sheet");
  const [companyName, setCompanyName] = useState("");
  const [closingBalance, setClosingBalance] = useState("");
  const [pdfURL, setpdfURL] = useState();
  const [fromDate, setFromDate] = useState(() =>
    format(startOfMonth(new Date()), "yyyy-MM-dd"),
  );
  const [toDate, setToDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [paymentType, setPaymentType] = useState({
    label: "All",
    value: "",
  });
  const [data, setData] = useState<any>({ paymentsArray: [], total: null });
  const [limit, setLimit] = useState(150);
  const [totalRows, setTotalRows] = useState<any>();
  const [error, setError] = useState("");

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);

    if (toDate && newFromDate > toDate) {
      setError("From date cannot be later than To date.");
    } else {
      setError("");
    }
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newToDate = e.target.value;
    setToDate(newToDate);

    if (fromDate && newToDate < fromDate) {
      setError("To date cannot be earlier than From date.");
    } else {
      setError("");
    }
  };

  const fetchPayments = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    if (data.paymentsArray.length === 0) {
      setLoading(true);
    }

    try {
      let apiUrl = `balance-sheet?from_date=${fromDate}&to_date=${toDate}&payment_type=${paymentType.value}&limit=${limit}`;

      if (companyType === "Both") {
        apiUrl += `&supplier_id=${id}&buyer_id=${id}`;
      } else if (companyType === "Buyer") {
        apiUrl += `&buyer_id=${id}`;
      } else if (companyType === "Supplier") {
        apiUrl += `&supplier_id=${id}`;
      }
      const item = await apiGet(apiUrl);

      setpdfURL(item.pdf_url);
      setTotalRows(item.data.total);
      setCompanyName(item.data.company_name);
      setClosingBalance(item.data.balance_amount);
      const paymentsArray: any = Object.values(item.data.data);
      const total: any = paymentsArray.pop();

      setData({ paymentsArray, total });
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setToast("error", error.response.data.errors);
      } else if (error.response?.data?.message) {
        setToast("error", error.response.data.message);
      } else {
        setToast("error", "An error occurred while processing your request");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyType) {
      fetchPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, paymentType, limit, companyType]);

  const handleShowMore = () => {
    setLimit((prevLimit) => prevLimit + 150);
  };
  const handleDownloadPDF = () => {
    window.open(pdfURL, "_blank");
  };

  return (
    <div className="">
      {isBalanceSheetPage && (
        <Breadcrumb>
          <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
          <Breadcrumb.Item href="/BuyersAndSuppliers">
            Buyers & Suppliers
          </Breadcrumb.Item>
          <Breadcrumb.Item active className="fw-bold">
            Balance Sheet
          </Breadcrumb.Item>
        </Breadcrumb>
      )}
      <div className="d-flex justify-content-between align-items-center">
        <h2>Balance Sheet of {companyName}</h2>
        <div>
          <button
            type="button"
            className="btn btn-primary btn-sm mb-2 mx-2"
            onClick={handleDownloadPDF}
          >
            Download PDF
            <Download className="mx-2" style={{ fill: "white" }} />
          </button>
          <CustomButton
            label={`Back`}
            customeClass="mb-2"
            variant="secondary"
            onClick={() => navigate(-1)}
          />
        </div>
      </div>
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="d-flex">
              <div style={{ marginRight: "20px" }}>
                <label>From: </label>
                <input
                  className="form-control"
                  type="date"
                  value={fromDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={handleFromDateChange}
                />
              </div>
              <div style={{ marginRight: "20px" }}>
                <label>To: </label>
                <input
                  className="form-control"
                  type="date"
                  value={toDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={handleToDateChange}
                />
              </div>
              <div style={{ marginRight: "20px" }}>
                <label>Payment Type: </label>
                <Select
                  options={[
                    { label: "All", value: "" },
                    { label: "Credit", value: "credit" },
                    { label: "Debit", value: "debit" },
                  ]}
                  placeholder="Select options..."
                  onChange={(selectedOption: any) =>
                    setPaymentType(selectedOption)
                  }
                  value={paymentType}
                  className="p-0"
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      minWidth: "120px",
                    }),
                  }}
                />
              </div>
            </div>
            <div>
              <div className="fs-5">
                <span>Closing Balance: </span>
                <span>{closingBalance}</span>
              </div>
              <div className="fs-5">
                <span>Total Amount: </span>
                <span>{data.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
          {error && <span style={{ color: "red" }}>{error}</span>}

          {loading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : data.paymentsArray.length === 0 ? (
            <div className="text-center">
              <p>No Data Available</p>
            </div>
          ) : (
            <div>
              <Table className="table table-bordered">
                <thead className="table-heder">
                  <tr>
                    <th>#</th>
                    <th>Payment Document Number</th>
                    <th>
                      <>
                        {companyType === "Buyer" && <th>Invoice Number</th>}
                        {companyType === "Supplier" && <th>Purchase Number</th>}
                        {companyType === "Both" && <th>Document Number</th>}
                      </>
                    </th>
                    <th>Payment Type</th>
                    <th>Payment Mode</th>
                    <th>Payment Reference Number</th>
                    <th>Payment Date</th>
                    <th>Opening Balance</th>
                    <th>Amount</th>
                    <th>Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.paymentsArray?.map((detail: any, index: any) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{detail?.document_number}</td>
                      <>
                        {companyType === "Buyer" && (
                          <td>{detail?.tax_invoice?.document_number}</td>
                        )}
                        {companyType === "Supplier" && (
                          <td>{detail?.purchase_order?.document_number}</td>
                        )}
                        {companyType === "Both" && (
                          <td>
                            {detail?.tax_invoice?.document_number}
                            {detail?.purchase_order?.document_number}
                          </td>
                        )}
                      </>
                      <td
                        style={{ textTransform: "capitalize" }}
                        className={"text-capitalize"}
                      >
                        {detail.payment_type}
                      </td>
                      <td className="text-capitalize">{detail.payment_mode}</td>
                      <td>{detail.payment_reference_number}</td>
                      <td>{detail.payment_date}</td>
                      <td className="text-end">
                        {Number(detail?.opening_balance)?.toFixed(2)}
                      </td>
                      <td
                        className={`${
                          detail.amount >= 0
                            ? "bg-success p-2 text-dark bg-opacity-50"
                            : "bg-danger text-dark bg-opacity-50"
                        } text-end`}
                      >
                        {detail.amount > 0
                          ? `+${Number(detail.amount).toFixed(2)}`
                          : Number(detail.amount).toFixed(2)}
                      </td>
                      <td className="text-end">
                        {Number(detail?.closing_balance)?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={9} className="fw-bold text-end">
                      Total:
                    </td>
                    <td className="fw-bold text-end">
                      {data?.total?.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </Table>
              {data.paymentsArray.length < totalRows && (
                <span
                  className="d-flex justify-content-end text-primary cursor-pointer"
                  role="button"
                  onClick={handleShowMore}
                >
                  show more...
                </span>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default BalanceSheet;
